<template>
    <div class="change-password-card">
        <div class="change-password-card__header">
            <div class="text-right" v-if="!show">
                <ui-button variant="secondary" @click="showForm" :disabled="disabled">
                    {{ $t('Update') }}
                </ui-button>
            </div>
            <div class="text-right" v-else>
                <ui-button variant="secondary" @click="close" :disabled="disabled">
                    {{ $t('Cancel') }}
                </ui-button>
            </div>
            <div class="change-password-card__content">
                <div class="change-password-card__icon">
                    <span class="fas fa-key"></span>
                </div>
                <div class="change-password-card__text-container">
                    <h6 class="mb-0">{{ $t('Change password') }}</h6>
                    <p class="mb-0">{{ $t('Update your password to keep your account secure.') }}</p>
                    <div v-if="disabled">
                        <p class="change-email__empty-message text-muted">
                            <span class="fas fa-info-circle"></span>
                            {{ $t('Your account was created with Google. To change your email, please create a password for your account.') }}
                        </p>
                    </div>
                    <div v-if="show && !disabled">
                        <form @submit.prevent="updatePassword" class="m-1">
                            <div class="mb-2">
                                <ui-input
                                    id="password"
                                    v-model="formData.old_password"
                                    name="password"
                                    type="password"
                                    placeholder="********"
                                    :label="$t('Current Password')"
                                    :field="v$.formData.password"
                                />
                            </div>
                            <div class="mb-2">
                                <ui-input
                                    id="password"
                                    v-model="formData.password"
                                    name="password"
                                    type="password"
                                    placeholder="********"
                                    :label="$t('Password')"
                                    :field="v$.formData.password"
                                />
                            </div>
                            <div class="mb-3">
                                <ui-input
                                    id="passwordRepeat"
                                    v-model="formData.password_confirmation"
                                    name="passwordRepeat"
                                    type="password"
                                    placeholder="********"
                                    :label="$t('Confirm password')"
                                    :field="v$.formData.password_confirmation"
                                />
                            </div>
                            <div>
                                <ui-button class="mt-2" type="submit" variant="primary" :disabled="disabled" :loading="loading">
                                    {{ $t('Update password') }}
                                </ui-button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { helpers, maxLength, minLength, required, sameAs } from '@vuelidate/validators';
import UiButton from '@/components/ui/buttons/Button.vue';
import UiInput from '@/components/ui/inputs/Input.vue';

export default {
    name: 'ChangesPasswordCard',
    components: { UiButton, UiInput },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            disabled: false,
            show: false,
            account: {},
            formData: {
                old_password: '',
                password: '',
                password_confirmation: '',
            },
        };
    },
    validations() {
        const passwordRules = {
            minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(8)),
            maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(25)),
            valid: helpers.withMessage(this.$t('Password must include upper and lower case letters, numbers, and special characters'), value => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[[\]@$!%*¡¿?&#=|.(){}:;<>,/~_+-])([A-Za-z\d[\]@$!%*¡¿?&#=|.(){}:;<>,/~_+-]|[^ ]){8,25}$/.test(value)),
            $autoDirty: true,
        };
        return {
            formData: {
                old_password: {
                    required: helpers.withMessage(this.$t('validations.required.female', { field: this.$t('current password') }), required),
                    ...passwordRules,
                },
                password: {
                    required: helpers.withMessage(this.$t('validations.required.female', { field: this.$t('password') }), required),
                    ...passwordRules,
                },
                password_confirmation: {
                    ...passwordRules,
                    required: helpers.withMessage(this.$t('validations.required.female', { field: this.$t('password confirmation') }), required),
                    sameAs: helpers.withMessage(this.$t('Passwords do not match'), sameAs(this.formData.password)),
                }
            },
        };
    },
    mounted() {
        this.getAccount();
    },
    methods: {
        async showForm() {
            this.show = true;
        },
        async close() {
            this.show = false;
        },
        async updatePassword() {
            try {
                this.v$.$touch();
    
                if (this.v$.$invalid) {
                    return;
                }

                this.loading = true;
                
                await this.axiosAccount.put('/api/accounts/change/recovery', {}, {
                    headers: {
                        Authorization: `Basic ${this.account.email}:${this.formData.old_password}`,
                        'x-user-secret': Buffer.from(this.formData.password).toString('base64'),
                    }
                });
                this.$toasted.global.successMessage(this.$t('Password updated successfully'));
                setTimeout(() => {
                    this.close();
                }, 500);
            } catch (error) {
                const status = error?.response?.status;

                if (status === 422) {
                    this.$toasted.global.errorMessage(this.$t('The password must not be the same to earlier'));
                    return;
                }

                this.showError(error);
            } finally {
                setTimeout(() => {
                    this.loading = false;
                    this.cleanForm();
                }, 500);
            }
        },
        cleanForm() {
            this.formData.old_password = '';
            this.formData.password = '';
            this.formData.password_confirmation = '';
            this.v$.$reset();
        },
        async getAccount() {
            try {
                this.loadingAccount = true;
                const { data } = await this.axiosAccount.get('/api/accounts/me');
                this.account = data;
                this.formData.email = this.account.email;
                this.disabled = this.account.account_type === 'social';
            } catch (error) {
                this.showError(error);
            } finally {
                this.loadingAccount = false;
            }
        }
    }
}
</script>

<style lang="scss">
.change-password-card {
    align-items: center;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;

    &__header {
        display: flex;
        flex-direction: column;
        gap: 16px;
        justify-content: space-between;
        width: 100%;

        @media (min-width: 768px) {
            flex-direction: row-reverse;
            gap: 32px;
        }
    }

    &__content {
        display: flex;
        gap: 8px;
        width: 100%;
    }

    &__icon {
        align-items: center;
        background-color: #71D2D6;
        border-radius: 8px;
        color: $white;
        display: flex;
        flex-shrink: 0;
        font-size: 1rem;
        height: 30px;
        justify-content: center;
        width: 30px;

        @media (min-width: 768px) {
            height: 50px;
            width: 50px;
            font-size: 1.375rem;
        }
    }

    &__button {
        align-items: center;
        background-color: transparent;
        border: 1px solid #12B4BA;
        border-radius: 8px;
        color: #12B4BA;
        display: inline-flex;
        flex-shrink: 0;
        gap: 8px;
        height: 32px;
        padding: 8px 16px;

        &:hover {
            background-color: #12B4BA;
            color: $white;
        }
    }

    &__empty-message {
        background-color: #eff1f8;
        border-radius: 8px;
        padding: 16px;
        text-align: center;
        width: 100%;
        margin-bottom: 0;
    }

    &__device-list {
        border-radius: 8px;
        padding: 16px;
        text-align: center;
        width: 100%;
        margin-bottom: 0;
    }
}
</style>